import { useEffect, useRef, useState } from 'react';

import { CheckIcon, CopyIcon } from './icons';

// ═══════════════════════════════════════════════════════════════════════════════
// Tree View (root)
// ═══════════════════════════════════════════════════════════════════════════════

export function DataTreeView({
  data,
  searchQuery,
}: {
  data: unknown;
  searchQuery: string;
}) {
  // Toggling this counter forces all TreeNodes to reset their expanded state.
  // Even numbers = expand all, odd = collapse all. Starts at 0 (expanded).
  const [expandSignal, setExpandSignal] = useState(0);

  if (data === null || data === undefined) {
    return <span className="text-tertiary italic">null</span>;
  }

  if (typeof data !== 'object') {
    return <ValueDisplay value={data} />;
  }

  const entries = Array.isArray(data)
    ? data.map((v, i) => [String(i), v] as const)
    : Object.entries(data as Record<string, unknown>);

  if (entries.length === 0) {
    return (
      <span className="text-tertiary italic">
        {Array.isArray(data) ? '[]' : '{}'}
      </span>
    );
  }

  const isAllExpanded = expandSignal % 2 === 0;

  return (
    <div className="font-mono text-xs">
      <div className="mb-1 flex gap-2">
        <button
          className="text-secondary hover:text-emphasis cursor-pointer text-[10px] font-semibold"
          onClick={() => setExpandSignal((s) => (s % 2 === 0 ? s + 2 : s + 1))}
        >
          Expand All
        </button>
        <span className="text-tertiary">·</span>
        <button
          className="text-secondary hover:text-emphasis cursor-pointer text-[10px] font-semibold"
          onClick={() => setExpandSignal((s) => (s % 2 === 1 ? s + 2 : s + 1))}
        >
          Collapse All
        </button>
      </div>
      {entries.map(([key, value]) => (
        <TreeNode
          key={key}
          nodeKey={key}
          value={value}
          searchQuery={searchQuery}
          depth={0}
          expandSignal={expandSignal}
          defaultExpanded={isAllExpanded}
        />
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Tree Node (recursive)
// ═══════════════════════════════════════════════════════════════════════════════

function TreeNode({
  nodeKey,
  value,
  searchQuery,
  depth,
  expandSignal,
  defaultExpanded,
}: {
  nodeKey: string;
  value: unknown;
  searchQuery: string;
  depth: number;
  expandSignal: number;
  defaultExpanded: boolean;
}) {
  const isObject = value !== null && typeof value === 'object';
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Reset expand state when the global expand/collapse signal changes
  const prevSignal = useRef(expandSignal);
  useEffect(() => {
    if (expandSignal !== prevSignal.current) {
      prevSignal.current = expandSignal;
      setIsExpanded(expandSignal % 2 === 0);
    }
  }, [expandSignal]);

  const lowerSearch = searchQuery.toLowerCase();
  const isKeyMatch =
    searchQuery !== '' && nodeKey.toLowerCase().includes(lowerSearch);
  const isValueMatch =
    searchQuery !== '' &&
    !isObject &&
    String(value).toLowerCase().includes(lowerSearch);
  const isHighlighted = isKeyMatch || isValueMatch;

  // Auto-expand when a search query is active
  useEffect(() => {
    if (searchQuery && isObject) {
      setIsExpanded(true);
    }
  }, [searchQuery, isObject]);

  const indent = depth * 16;

  // ── Leaf node ──
  if (!isObject) {
    return (
      <div
        data-search-match={isHighlighted ? '' : undefined}
        className={`group/row hover:bg-fillHover flex items-baseline gap-1 rounded py-px ${isHighlighted ? 'bg-fillHighlight' : ''}`}
        style={{ paddingLeft: indent }}
      >
        {/* Empty spacer matching the arrow width so keys align with parent */}
        <span className="w-3 shrink-0" />
        <span className="text-info shrink-0">{nodeKey}</span>
        <span className="text-tertiary">:</span>
        <ValueDisplay value={value} />
        <CopyNodeButton value={value} />
      </div>
    );
  }

  // ── Object / Array node ──
  const isArray = Array.isArray(value);
  const entries = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>);
  const count = entries.length;
  const typeLabel = isArray ? `[${count}]` : `{${count}}`;

  return (
    <>
      <div
        data-search-match={isHighlighted ? '' : undefined}
        className={`group/row hover:bg-fillHover flex cursor-pointer items-baseline gap-1 rounded py-px ${isHighlighted ? 'bg-fillHighlight' : ''}`}
        style={{ paddingLeft: indent }}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setIsExpanded(!isExpanded);
        }}
      >
        <span className="text-tertiary w-3 shrink-0 text-center">
          {isExpanded ? '▾' : '▸'}
        </span>
        <span className="text-info shrink-0">{nodeKey}</span>
        <span className="text-tertiary shrink-0">{typeLabel}</span>
        {!isExpanded && count > 0 ? (
          <span className="text-tertiary truncate text-[10px] italic">
            {getPreview(value)}
          </span>
        ) : null}
        <CopyNodeButton value={value} />
      </div>
      {isExpanded
        ? entries.map(([k, v]) => (
            <TreeNode
              key={k}
              nodeKey={k}
              value={v}
              searchQuery={searchQuery}
              depth={depth + 1}
              expandSignal={expandSignal}
              defaultExpanded={defaultExpanded}
            />
          ))
        : null}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Value Display
// ═══════════════════════════════════════════════════════════════════════════════

function ValueDisplay({ value }: { value: unknown }) {
  if (value === null)
    return <span className="text-secondary italic">null</span>;
  if (value === undefined)
    return <span className="text-secondary italic">undefined</span>;
  if (typeof value === 'boolean')
    return (
      <span className="text-highlight font-semibold">{String(value)}</span>
    );
  if (typeof value === 'number')
    return <span className="text-positive">{String(value)}</span>;
  if (typeof value === 'string') {
    const display = value.length > 120 ? `${value.slice(0, 120)}…` : value;
    return (
      <span className="text-caution break-all">&quot;{display}&quot;</span>
    );
  }
  return <span className="text-secondary">{String(value)}</span>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Copy Node Button (appears on row hover)
// ═══════════════════════════════════════════════════════════════════════════════

function CopyNodeButton({ value }: { value: unknown }) {
  const [hasCopied, setHasCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text =
      typeof value === 'object' && value !== null
        ? JSON.stringify(value, null, 2)
        : String(value);
    void navigator.clipboard.writeText(text);
    setHasCopied(true);
    setTimeout(() => setHasCopied(false), 1200);
  };

  return (
    <button
      className="text-secondary ml-2 flex shrink-0 cursor-pointer items-center gap-1 rounded border px-1.5 py-px text-[10px] font-semibold opacity-0 transition-opacity group-hover/row:opacity-100"
      onClick={handleCopy}
      title="Copy value"
    >
      {hasCopied ? (
        <>
          <CheckIcon size={12} />
          <span className="text-positive">Copied</span>
        </>
      ) : (
        <>
          <CopyIcon size={12} />
          <span>Copy</span>
        </>
      )}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════════

function getPreview(value: unknown): string {
  if (Array.isArray(value)) {
    const items = value.slice(0, 3).map((v) => {
      if (typeof v === 'string') return `"${v.slice(0, 20)}"`;
      if (typeof v === 'object' && v !== null) return '{…}';
      return String(v);
    });
    return items.join(', ') + (value.length > 3 ? ', …' : '');
  }

  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj);
  const items = keys.slice(0, 3).map((k) => {
    const v = obj[k];
    if (typeof v === 'string') return `${k}: "${v.slice(0, 15)}"`;
    if (typeof v === 'object' && v !== null) return `${k}: {…}`;
    return `${k}: ${String(v)}`;
  });
  return items.join(', ') + (keys.length > 3 ? ', …' : '');
}
